import { useEffect, useState } from 'react'
import { supabase, isDemoMode } from '@/lib/supabase'
import { useAuthContext } from '@/contexts/AuthContext'

interface LogoProps {
    className?: string;
    iconOnly?: boolean;
    size?: 'sm' | 'md' | 'lg';
}

// Cache the logo URL to avoid re-fetching on every render
let cachedLogoUrl: string | null | undefined = undefined
let cachedCenterId: string | null = null

export default function Logo({ className = '', iconOnly = false, size = 'md' }: LogoProps) {
    const { user } = useAuthContext()
    const [centerLogoUrl, setCenterLogoUrl] = useState<string | null>(cachedLogoUrl ?? null)

    const sizes = {
        sm: { icon: 24, img: 'h-6', text: 'text-[16px]', gap: 'gap-1.5' },
        md: { icon: 32, img: 'h-8', text: 'text-[22px]', gap: 'gap-2.5' },
        lg: { icon: 40, img: 'h-10', text: 'text-[28px]', gap: 'gap-3' },
    }
    const s = sizes[size]

    useEffect(() => {
        const centerId = user?.establishmentId
        if (!centerId || isDemoMode) return
        // Use cache if same center
        if (cachedCenterId === centerId && cachedLogoUrl !== undefined) {
            setCenterLogoUrl(cachedLogoUrl)
            return
        }
        supabase
            .from('training_centers')
            .select('logo_url')
            .eq('id', centerId)
            .single()
            .then(({ data }) => {
                const url = data?.logo_url || null
                cachedLogoUrl = url
                cachedCenterId = centerId
                setCenterLogoUrl(url)
            })
    }, [user?.establishmentId])

    // Listen for logo changes (from settings page)
    useEffect(() => {
        const handler = (e: CustomEvent) => {
            cachedLogoUrl = e.detail
            setCenterLogoUrl(e.detail)
        }
        window.addEventListener('center-logo-changed', handler as EventListener)
        return () => window.removeEventListener('center-logo-changed', handler as EventListener)
    }, [])

    // If center has a custom logo, show it instead
    if (centerLogoUrl) {
        return (
            <div className={`flex items-center ${s.gap} ${className}`}>
                <img
                    src={centerLogoUrl}
                    alt="Logo"
                    className={`${s.img} w-auto object-contain flex-shrink-0`}
                    style={{ maxWidth: iconOnly ? `${s.icon}px` : '160px' }}
                />
            </div>
        )
    }

    // Default AntiPlanning logo
    return (
        <div className={`flex items-center ${s.gap} ${className}`}>
            <svg
                width={s.icon}
                height={s.icon}
                viewBox="0 0 40 40"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="flex-shrink-0"
            >
                <defs>
                    <linearGradient id="logo-gradient" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#FF5B46" />
                        <stop offset="100%" stopColor="#FBA625" />
                    </linearGradient>
                    <linearGradient id="logo-gradient-light" x1="0" y1="0" x2="40" y2="40" gradientUnits="userSpaceOnUse">
                        <stop offset="0%" stopColor="#FF5B46" stopOpacity="0.15" />
                        <stop offset="100%" stopColor="#FBA625" stopOpacity="0.15" />
                    </linearGradient>
                </defs>

                {/* Background rounded square */}
                <rect x="2" y="2" width="36" height="36" rx="10" fill="url(#logo-gradient)" />

                {/* Calendar body (white) */}
                <rect x="8" y="11" width="18" height="16" rx="3" fill="white" fillOpacity="0.95" />

                {/* Calendar header bar */}
                <rect x="8" y="11" width="18" height="5.5" rx="3" fill="white" fillOpacity="0.6" />
                <line x1="8" y1="16.5" x2="26" y2="16.5" stroke="white" strokeOpacity="0.3" strokeWidth="0.5" />

                {/* Calendar pins */}
                <rect x="12.5" y="9" width="2" height="4" rx="1" fill="white" />
                <rect x="19.5" y="9" width="2" height="4" rx="1" fill="white" />

                {/* Event blocks inside calendar */}
                <rect x="10.5" y="18.5" width="5" height="2.5" rx="1" fill="#FF5B46" fillOpacity="0.7" />
                <rect x="17" y="18.5" width="5.5" height="2.5" rx="1" fill="#FBA625" fillOpacity="0.7" />
                <rect x="10.5" y="22.5" width="8" height="2.5" rx="1" fill="#3b82f6" fillOpacity="0.5" />

                {/* Breaking arrow — escaping the calendar (top-right) */}
                <path
                    d="M27 12L33 6M33 6H28.5M33 6V10.5"
                    stroke="white"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />

                {/* Spark dots around the arrow */}
                <circle cx="30" cy="9" r="1" fill="white" fillOpacity="0.5" />
                <circle cx="35" cy="11" r="0.8" fill="white" fillOpacity="0.4" />
            </svg>

            {!iconOnly && (
                <div className="flex flex-col leading-none">
                    <span
                        className={`font-extrabold ${s.text} tracking-tight text-current whitespace-nowrap`}
                        style={{ fontFamily: 'Inter, sans-serif' }}
                    >
                        Anti<span style={{ color: '#FF5B46' }}>Planning</span>
                    </span>
                </div>
            )}
        </div>
    );
}
